import { Feature } from 'ol';
import { MultiPolygon, Polygon } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { Coordinate, layer } from 'openlayers';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WKT from 'ol/format/WKT';
import { FeatureLike } from 'ol/Feature';
import { Box, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import { useLine3DNavigationStore } from '../stores/useLine3DNavigationStore';
import { useMapStore } from '../stores/useMapStore';
import { useSeismicStore } from '../stores/useSeismicStore';
import { calculateXPosition, getAmplitude, getTrace, getXByScale, getYByAmplitude, getYByScale } from '../utils/calculateScreenValuesUtils';
import { LineType } from '../models/enums/LineType';
import { SampleIntervalUnit } from '../models/enums/SampleIntervalUnity';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { IVolumeDebugInfo } from '../models/interfaces/IVolumeDebugInfo';
import { RestrictedRoute } from 'features/auth/RestrictedRoute';

export const VolumeDebugPanel = () => {
    const volumeInfoRef = useRef<HTMLDivElement | null>(null);
    const featureRef = useRef<Feature>();
    const layerRef = useRef<VectorLayer<VectorSource>>();
    const currentHoveredVolumeFeatureRef = useRef<FeatureLike | undefined>();
    const volumeFeaturesRef = useRef<Feature<Polygon>[]>([]);

    const { current: showRestrictedArea } = useRef(getParamsFromUrl().showRestrictedArea);

    const lineType = useLine3DNavigationStore(state => state.lineType);
    const map = useMapStore(state => state.map);

    const [ selectedVolumeInfo, setSelectedVolumeInfo ] = useState<IVolumeDebugInfo>();

    const { surveyMetadata, isInverted, scale } = useSeismicStore(state => ({
        surveyMetadata: state.surveyMetadata,
        isInverted: state.isInverted,
        scale: state.scale,
    }));

    const restrictedAreaGeometry  = useMemo(() => !!surveyMetadata?.RestrictedAreaWKT ? new WKT().readGeometry(surveyMetadata.RestrictedAreaWKT) as MultiPolygon : null, [surveyMetadata]);
    const volumeDebugInfo = useMemo(() => surveyMetadata?.VolumeDebugInfo, [surveyMetadata]);

    const previousScaleRef = useRef(scale);

    const surveyLineStart = useMemo(() => lineType === LineType.Xline ? surveyMetadata?.Survey3DInfo.InlineStart : surveyMetadata?.Survey3DInfo.XlineStart, [lineType, surveyMetadata]);
    const surveyLineEnd = useMemo(() => lineType === LineType.Xline ? surveyMetadata?.Survey3DInfo.InlineEnd : surveyMetadata?.Survey3DInfo.XlineEnd, [lineType, surveyMetadata]);
    const lineIncrement = useMemo(() => lineType === LineType.Xline ? surveyMetadata?.Survey3DInfo.InlineIncrement : surveyMetadata?.Survey3DInfo.XlineIncrement, [lineType, surveyMetadata]);
    const sampleInterval = useMemo(() =>
        surveyMetadata?.Header.SampleIntervalUnit === SampleIntervalUnit.Microsecond ? Math.ceil(surveyMetadata.Header.SampleInterval / 1000) : surveyMetadata?.Header.SampleInterval,
    [surveyMetadata]);

    const totalTraces = useMemo(() => !!surveyMetadata ? (surveyLineEnd! - surveyLineStart!) / lineIncrement! : 0, [surveyMetadata, surveyLineEnd, surveyLineStart, lineIncrement]);

    const displayVolumeInfo = useCallback((pixel: number[], target: HTMLElement) => {
        const volumeInfoElement = volumeInfoRef.current;
        if (!map || !volumeInfoElement) {
            return;
        }

        const mapElement = map.getTargetElement();

        if (!mapElement) {
            return;
        }

        const feature = target.closest('.ol-control') ?
            undefined
            :
            map.forEachFeatureAtPixel(pixel, function (feature) {
                return feature;
            });

        if (!!feature) {
            const mapRect = mapElement.getBoundingClientRect();
            volumeInfoElement.style.left = `${pixel[0] + mapRect.left}px`;
            volumeInfoElement.style.top = `${pixel[1] + mapRect.top}px`;
            if (feature !== currentHoveredVolumeFeatureRef.current) {
                const volumeInfo = feature.get('volumeInfo') as IVolumeDebugInfo;
                if (!!volumeInfo) {
                    setSelectedVolumeInfo(volumeInfo);
                }
            }

        }
        else {
            setSelectedVolumeInfo(undefined);
        }

        currentHoveredVolumeFeatureRef.current = feature;
    }, [map]);

    useEffect(() => {
        if (!layerRef.current || !volumeDebugInfo) {
            return;
        }

        const scaledPolygons: Polygon[] = [];
        const feature = featureRef.current;
        //const polygons = (feature.getGeometry() as MultiPolygon).getPolygons();
        const polygons = volumeFeaturesRef.current.map(x => x.getGeometry() as Polygon);
        const scaledFeatures : Feature<Polygon>[] = [];

        polygons.forEach((polygon, i) => {
            const scaledCoordinates: Coordinate[] = [];

            const volumeInfo = volumeDebugInfo[i];

            polygon.getCoordinates()[0].forEach(coordinate => {
                const traceX = getTrace(coordinate[0], previousScaleRef.current.x, surveyLineStart!, lineIncrement!);
                const amplitudeY = parseInt(getAmplitude(coordinate[1], previousScaleRef.current.y, sampleInterval!));
                const scaledX = calculateXPosition(traceX, totalTraces, surveyLineStart!, surveyLineEnd!, lineIncrement!, surveyLineStart!, false, scale.x);
                const scaledY = getYByAmplitude(amplitudeY, scale.y, sampleInterval!);
                scaledCoordinates.push([scaledX, scaledY]);
            });

            const feature = new Feature({
                geometry: new Polygon([scaledCoordinates]),
                style: new Style({
                    stroke: new Stroke({
                        color: '#18458c',
                        width: 2
                    }),
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0)'
                    })
                })
            });

            feature.set('volumeInfo', volumeInfo);

            scaledFeatures.push(feature);
        });

        const source = layerRef.current.getSource();

        volumeFeaturesRef.current.forEach(feature => source?.removeFeature(feature));
        scaledFeatures.forEach(feature => source?.addFeature(feature));

        volumeFeaturesRef.current = scaledFeatures;

        layerRef.current.changed();
        previousScaleRef.current = scale;
    }, [scale]);

    useEffect(() => {
        if (!surveyMetadata || !map || !restrictedAreaGeometry || !showRestrictedArea || !volumeDebugInfo) {
            return;
        }

        const restrictedAreaLayerSource = new VectorSource();
        const restrictedAreaLayer = new VectorLayer({ source: restrictedAreaLayerSource });

        const features : Feature<Polygon>[] = [];

        restrictedAreaGeometry.getPolygons().forEach((polygon, i) => {
            const volumeInfo = volumeDebugInfo[i];

            const scaledCoordinates: Coordinate[] = [];
            polygon.getCoordinates()[0].forEach(coordinate => scaledCoordinates.push([
                calculateXPosition(coordinate[0], totalTraces, surveyLineStart!, surveyLineEnd!, lineIncrement!, surveyLineStart!, isInverted, scale.x),
                getYByAmplitude(coordinate[1], scale.y, Math.ceil(sampleInterval!))
            ]));
            const scaledPolygon = new Polygon([scaledCoordinates]);

            const feature = new Feature({
                geometry: scaledPolygon,
                style: new Style({
                    stroke: new Stroke({
                        color: '#18458c',
                        width: 2
                    }),
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0)'
                    })
                })
            });
            feature.set('volumeInfo', volumeInfo);
            restrictedAreaLayerSource.addFeature(feature);
            features.push(feature);
        });

        layerRef.current = restrictedAreaLayer;
        previousScaleRef.current = scale;
        map.addLayer(restrictedAreaLayer);
        restrictedAreaLayer.set('isInverted', false);

        const mapElement = map.getTargetElement();
        const volumeInfoElement = volumeInfoRef.current;

        volumeFeaturesRef.current = features;

        if (!volumeInfoElement) {
            return;
        }

        map.on('pointermove', (evt) => {
            if (evt.dragging) {
                setSelectedVolumeInfo(undefined);
                currentHoveredVolumeFeatureRef.current = undefined;
                return;
            }
            displayVolumeInfo(evt.pixel, evt.originalEvent.target);
        });

    }, [surveyMetadata, map, restrictedAreaGeometry]);

    useEffect(() => {
        if (!layerRef.current || !surveyMetadata || !volumeDebugInfo) {
            return;
        }
        const restrictedAreaLayer = layerRef.current;
        const restrictedAreaLayerSource = restrictedAreaLayer.getSource();

        if (!!restrictedAreaLayer) {
            const isGeometryInverted = restrictedAreaLayer!.get('isInverted') as boolean;
            if (isGeometryInverted !== isInverted) {
                const polygons = volumeFeaturesRef.current.map(x => x.getGeometry() as Polygon);
                const flippedVolumeFeatures: Feature<Polygon>[] = [];

                polygons.forEach((polygon, i) => {
                    const flippedCoordinates: Coordinate[] = [];
                    const coordinates = polygon.getCoordinates()[0];

                    const volumeInfo = volumeDebugInfo[i];

                    coordinates.forEach(coordinate => {
                        const xTrace = getTrace(coordinate[0], scale.x, surveyLineStart!, lineIncrement!);
                        const flippedX = calculateXPosition(xTrace, totalTraces, surveyLineStart!, surveyLineEnd!, lineIncrement!, surveyLineStart!, true, scale.x);
                        const y = coordinate[1];
                        flippedCoordinates.push([flippedX, y]);
                    });
                    const feature = new Feature({
                        geometry: new Polygon([flippedCoordinates]),
                        style: new Style({
                            stroke: new Stroke({
                                color: '#18458c',
                                width: 2
                            }),
                            fill: new Fill({
                                color: 'rgba(255, 255, 255, 0)'
                            })
                        })
                    });
                    feature.set('volumeInfo', volumeInfo);
                    flippedVolumeFeatures.push(feature);
                });

                //const flippedRestrictedAreaGeometry = new MultiPolygon(flippedPolygons);
                volumeFeaturesRef.current.forEach(feature => restrictedAreaLayerSource?.removeFeature(feature));
                flippedVolumeFeatures.forEach(feature => restrictedAreaLayerSource?.addFeature(feature));

                volumeFeaturesRef.current = flippedVolumeFeatures;
                restrictedAreaLayerSource!.changed();
                restrictedAreaLayer!.set('isInverted', isInverted);
            }
        }
    }, [isInverted]);

    return (
        <Box
            ref={volumeInfoRef}
            id='info'
            style={{
                position: 'absolute',
                display: 'inline-block',
                height: 'auto',
                width: 'auto',
                zIndex: 999999999,
                backgroundColor: '#333',
                color: '#fff',
                textAlign: 'center',
                borderRadius: '4px',
                padding: '5px',
                visibility: !!selectedVolumeInfo ? 'visible' : 'hidden',
                opacity: !!selectedVolumeInfo ? 1 : 0,
                pointerEvents: 'none',
                maxWidth: '200px',
                transition: 'opacity 500ms'
            }}
        >
            {
                <Stack direction={'column'} gap={2}>
                    <Typography>Display name: {selectedVolumeInfo?.DisplayName}</Typography>
                    <Typography>Item Token: {selectedVolumeInfo?.ItemToken}</Typography>
                    <Typography>Volume token: {selectedVolumeInfo?.VolumeToken}</Typography>
                    <Typography>Start trace: {selectedVolumeInfo?.StartTrace}</Typography>
                    <Typography>End trace: {selectedVolumeInfo?.EndTrace}</Typography>
                    <Typography>Item Start sample: {selectedVolumeInfo?.StartSample}</Typography>
                    <Typography>File End sample: {selectedVolumeInfo?.EndSample}</Typography>
                    <Typography>Start time: {selectedVolumeInfo?.StartTime}</Typography>
                    <Typography>End time: {selectedVolumeInfo?.EndTime}</Typography>
                    <Typography>Sample Interval: {selectedVolumeInfo?.SampleInterval}</Typography>
                    <Typography>Reference Sample Interval: {selectedVolumeInfo?.ReferenceSampleInterval}</Typography>
                    <Typography>Resampled Sample Start: {selectedVolumeInfo?.AdjustedSampleStart}</Typography>
                    <Typography>Resampled Sample End: {selectedVolumeInfo?.AdjustedSampleEnd}</Typography>
                </Stack>
            }
        </Box>
    );
};